import React, { useState, useEffect, useMemo } from 'react';
import Modal from '../ui/Modal';
import { createSoftSchedule } from '../../api/client';
import { buildApiUrl, apiConfig } from '../../apiConfig';

function toUtcIso(dateStr) {
  if (!dateStr) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dateStr));
  if (!m) return null;
  return `${m[1]}-${m[2]}-${m[3]}T00:00:00Z`;
}

function fmt(d) {
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const INPUT = { padding: '5px 8px', background: '#fff', color: '#000', border: '1px solid #ccc', borderRadius: 4, fontSize: 13 };
const LABEL = { fontWeight: 600, marginBottom: 4, color: '#e3f2fd', fontSize: 13 };

/**
 * Lightweight soft schedule creation modal for the utilization view.
 * Does not depend on ScheduleDataContext — fetches users directly.
 *
 * @param {object}  presetTicket   - { ticketId, ticketNumber, summary, projectName, projectId }
 * @param {object}  presetPerson   - { memberIdentifier, memberName }
 * @param {string}  defaultDateStart - ISO date (YYYY-MM-DD) for the week start
 */
export default function UtilizationSoftScheduleModal({
  open, onClose, onSuccess, token,
  presetTicket = null, presetPerson = null,
  defaultDateStart = null,
}) {
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [searchUser, setSearchUser] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);

  const [ticketIdInput, setTicketIdInput] = useState('');
  const [description, setDescription] = useState('');
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [hours, setHours] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Reset and populate form on open
  useEffect(() => {
    if (!open) return;
    setError(null);
    setDescription('');
    setHours('');
    setSearchUser('');

    // Default dates to the week window
    if (defaultDateStart) {
      const start = new Date(defaultDateStart + 'T00:00:00');
      const end = new Date(start);
      end.setDate(end.getDate() + 4); // Mon–Fri default
      setDateStart(fmt(start));
      setDateEnd(fmt(end));
    } else {
      const today = new Date();
      setDateStart(fmt(today));
      setDateEnd(fmt(today));
    }

    if (presetTicket) {
      setTicketIdInput(String(presetTicket.ticketId ?? ''));
    } else {
      setTicketIdInput('');
    }

    if (presetPerson) {
      setSelectedUser({ identifier: presetPerson.memberIdentifier, name: presetPerson.memberName, _preset: true });
    } else {
      setSelectedUser(null);
    }

    // Load full CW user list
    let cancelled = false;
    setLoadingUsers(true);
    (async () => {
      try {
        const url = buildApiUrl(apiConfig.cwUsersPath, { conditions: 'inactiveFlag=false' });
        const res = await fetch(url, { headers: { Accept: 'application/json', ...(token ? { Authorization: token } : {}) } });
        const data = res.ok ? await res.json() : [];
        const list = Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : []);
        if (!cancelled) {
          setUsers(list);
          if (presetPerson) {
            const match = list.find(u => u?.identifier === presetPerson.memberIdentifier || String(u?.id) === presetPerson.memberIdentifier);
            if (!cancelled) setSelectedUser(match ?? { identifier: presetPerson.memberIdentifier, name: presetPerson.memberName, _preset: true });
          }
        }
      } catch {
        if (!cancelled) setUsers([]);
      } finally {
        if (!cancelled) setLoadingUsers(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, token, presetPerson, presetTicket, defaultDateStart]);

  // Clear state on close
  useEffect(() => {
    if (!open) {
      setUsers([]); setSelectedUser(null); setSearchUser('');
      setTicketIdInput(''); setDescription('');
      setDateStart(''); setDateEnd(''); setHours(''); setError(null);
    }
  }, [open]);

  const filteredUsers = useMemo(() => {
    const n = searchUser.trim().toLowerCase();
    if (!n) return users;
    return users.filter(u => {
      const nm = (u?.name || u?.fullName || u?.identifier || '').toLowerCase();
      return nm.includes(n) || String(u?.id ?? '').includes(n);
    });
  }, [users, searchUser]);

  async function onSubmit() {
    try {
      setError(null);
      const memberIdentifier = selectedUser?.identifier || String(selectedUser?.id ?? '');
      if (!memberIdentifier) throw new Error('Select a user');
      if (!dateStart || !dateEnd) throw new Error('Enter start and end dates');
      const ds = toUtcIso(dateStart);
      const de = toUtcIso(dateEnd);
      if (!ds || !de) throw new Error('Invalid date format');
      if (new Date(de) < new Date(ds)) throw new Error('End date must be on or after start date');

      const payload = {
        memberIdentifier,
        dateStart: ds,
        dateEnd: de,
        ...(hours !== '' && Number(hours) > 0 ? { hours: Number(hours) } : { hours: 0 }),
      };

      const ticketIdStr = presetTicket ? String(presetTicket.ticketId ?? '') : ticketIdInput.trim();
      if (ticketIdStr && /^\d+$/.test(ticketIdStr)) {
        payload.ticketId = Number(ticketIdStr);
        if (presetTicket) {
          const projId = String(presetTicket.projectId ?? '');
          const projName = presetTicket.projectName || '';
          if (projId) payload.projectInfo = `${projId} • ${projName}`;
          const num = presetTicket.ticketNumber ? `#${presetTicket.ticketNumber}` : '';
          payload.ticketInfo = `${num} ${presetTicket.summary || ''}`.trim();
        }
      }
      // Description is stored in ProjectInfo (the backend has no description field)
      if (description.trim()) {
        payload.projectInfo = (payload.projectInfo ? `${payload.projectInfo} — ` : '') + description.trim();
      }
      if (!payload.ticketId && !payload.projectInfo) throw new Error('Enter a ticket ID or description');

      setSubmitting(true);
      const result = await createSoftSchedule(payload, token);
      onSuccess?.({ ...payload, ...(result ?? {}) });
      onClose?.();
    } catch (e) {
      setError(e);
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) return null;

  const canSubmit = !submitting && !!selectedUser && !!dateStart && !!dateEnd && (!!presetTicket || !!ticketIdInput.trim() || !!description.trim());

  return (
    <Modal onClose={onClose}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <h3 style={{ margin: 0, color: '#e3f2fd' }}>Add Soft Schedule</h3>
        <button onClick={() => onClose?.()} disabled={submitting} style={{ background: 'none', border: 'none', color: '#90caf9', cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>✕</button>
      </div>

      <div style={{ display: 'grid', gap: 14, color: '#e3f2fd' }}>
        {error && <div style={{ color: '#ef9a9a', fontSize: 13 }}>⚠ {error.message}</div>}

        {/* Ticket context or manual entry */}
        {presetTicket ? (
          <div style={{ background: '#1a3a3a', border: '1px solid #37474f', borderRadius: 5, padding: '8px 12px', fontSize: 13 }}>
            <div style={{ color: '#80cbc4', fontWeight: 700, marginBottom: 2 }}>Ticket</div>
            <div style={{ color: '#e3f2fd' }}>#{presetTicket.ticketNumber} — {presetTicket.summary}</div>
            {presetTicket.projectName && <div style={{ color: '#78909c', fontSize: 11, marginTop: 2 }}>{presetTicket.projectName}</div>}
          </div>
        ) : (
          <div>
            <div style={LABEL}>Ticket ID <span style={{ fontWeight: 400, color: '#78909c', fontSize: 11 }}>(optional if description given)</span></div>
            <input
              type="text"
              placeholder="e.g. 12345"
              value={ticketIdInput}
              onChange={e => setTicketIdInput(e.target.value)}
              style={{ ...INPUT, width: 140 }}
            />
          </div>
        )}

        {/* Description */}
        <div>
          <div style={LABEL}>Description <span style={{ fontWeight: 400, color: '#78909c', fontSize: 11 }}>(optional supplementary note)</span></div>
          <input
            type="text"
            placeholder={presetTicket ? 'Optional note…' : 'e.g. On-call coverage, Buffer time…'}
            value={description}
            onChange={e => setDescription(e.target.value)}
            style={{ ...INPUT, width: '100%', boxSizing: 'border-box' }}
          />
        </div>

        {/* Member */}
        <div>
          <div style={LABEL}>Member</div>
          {presetPerson && selectedUser && !selectedUser._preset && (
            <div style={{ fontSize: 12, color: '#80cbc4', marginBottom: 6 }}>
              Pre-selected: <strong>{selectedUser.name || selectedUser.identifier}</strong>
            </div>
          )}
          <input
            placeholder="Search by name or ID…"
            value={searchUser}
            onChange={e => setSearchUser(e.target.value)}
            style={{ ...INPUT, width: '100%', boxSizing: 'border-box', marginBottom: 6 }}
          />
          <div style={{ maxHeight: 150, overflowY: 'auto', border: '1px solid #ccc', borderRadius: 4, background: '#fff' }}>
            {loadingUsers && <div style={{ padding: '6px 10px', color: '#555', fontSize: 12 }}>Loading users…</div>}
            {!loadingUsers && filteredUsers.slice(0, 300).map(u => {
              const isSelected = selectedUser
                ? (selectedUser._preset ? selectedUser.identifier === u?.identifier : selectedUser.id === u?.id)
                : false;
              const name = u?.name || u?.fullName || u?.identifier || `User ${u?.id}`;
              return (
                <label key={u?.id ?? u?.identifier} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px', cursor: 'pointer', background: isSelected ? '#e3f2fd' : 'transparent' }}>
                  <input type="radio" name="util_soft_user" checked={isSelected} onChange={() => setSelectedUser(u)} style={{ cursor: 'pointer' }} />
                  <span style={{ color: '#000', fontSize: 12 }}>{u?.id ? `${u.id} • ` : ''}{name}</span>
                </label>
              );
            })}
            {!loadingUsers && filteredUsers.length === 0 && (
              <div style={{ padding: '6px 10px', color: '#888', fontSize: 12 }}>No users found.</div>
            )}
          </div>
        </div>

        {/* Dates + Hours */}
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <div style={LABEL}>Start date</div>
            <input type="date" value={dateStart} onChange={e => { setDateStart(e.target.value); setDateEnd(e.target.value); }} style={INPUT} />
          </div>
          <div>
            <div style={LABEL}>End date</div>
            <input type="date" value={dateEnd} onChange={e => setDateEnd(e.target.value)} style={INPUT} />
          </div>
          <div>
            <div style={LABEL}>Hours <span style={{ fontWeight: 400, color: '#78909c', fontSize: 11 }}>(optional)</span></div>
            <input
              type="number" min="0" step="0.5" placeholder="e.g. 8"
              value={hours}
              onChange={e => setHours(e.target.value)}
              style={{ ...INPUT, width: 90 }}
            />
          </div>
        </div>

        <button
          onClick={onSubmit}
          disabled={!canSubmit}
          style={{
            padding: '8px 18px',
            background: canSubmit ? '#1565c0' : '#455a64',
            border: 'none', borderRadius: 5,
            color: '#fff', fontWeight: 700, fontSize: 13,
            cursor: canSubmit ? 'pointer' : 'not-allowed',
            alignSelf: 'flex-start',
          }}
        >
          {submitting ? 'Creating…' : 'Create Soft Schedule'}
        </button>
      </div>
    </Modal>
  );
}
